import {
    ErrorActivity,
    RequestTranslationErrorGroup
} from '@/commons/constants/error'

export class BaseError extends Error {
    private readonly _translationGroup: string
    private _messageTranslationKeys: Array<string>
    private _titleTranslationKey: string
    private _errorActivity: number
    private readonly _errorPath: string
    private readonly _isStatic: boolean
    private _errorId: string

    constructor (
        messageTranslationKeys: Array<string>,
        titleTranslationKey: string,
        errorPath: string,
        errorActivity: number,
        message: string,
        _isStatic = false,
        translationGroup = '',
        errorId = ''
    ) {
        super(message)
        this._translationGroup = translationGroup
        this._isStatic = _isStatic
        this._errorPath = errorPath
        this._errorActivity = errorActivity
        this._errorId = errorId
        this._titleTranslationKey = titleTranslationKey
        this._messageTranslationKeys = messageTranslationKeys
    }

    get translationGroup (): string {
        return this._translationGroup
    }

    get errorPath (): string {
        return this._errorPath
    }

    get titleTranslationKey (): string {
        return this._titleTranslationKey
    }

    set titleTranslationKey (translationKey: string) {
        this._titleTranslationKey = translationKey
    }

    get errorActivity (): number {
        return this._errorActivity
    }

    set errorActivity (activity: number) {
        if (!this._isStatic) {
            this._errorActivity = activity
        }
    }

    get errorId (): string {
        return this._errorId
    }

    set errorId (_errorId: string) {
        if (!this._errorId) {
            this._errorId = _errorId
        }
    }

    get messageTranslationKeys (): Array<string> {
        return this._messageTranslationKeys
    }

    set messageTranslationKeys (translationKeys: Array<string>) {
        if (!this._isStatic) {
            this._messageTranslationKeys = translationKeys
        }
    }
}

export class GeneralError extends BaseError {
    constructor (
        messageTranslationKeys = ['errors.system.message'],
        titleTranslationKey = 'errors.system.title',
        errorPath = '/'
    ) {
        super(messageTranslationKeys, titleTranslationKey, errorPath, ErrorActivity.REDIRECT, 'Unspecified error')
    }
}

export class RequestGeneralError extends BaseError {
    constructor (
        messageTranslationKeys = ['errors.request.message'],
        titleTranslationKey = 'errors.request.title',
        errorPath = '/'
    ) {
        super(
            messageTranslationKeys,
            titleTranslationKey,
            errorPath,
            ErrorActivity.REDIRECT,
            'Unspecified request error',
            false,
            RequestTranslationErrorGroup.GENERAL
        )
    }
}

export class AuthenticationError extends BaseError {
    constructor (
        messageTranslationKeys = ['errors.system.authentication.message'],
        titleTranslationKey = 'errors.system.authentication.title',
        errorPath = '/logout'
    ) {
        super(messageTranslationKeys, titleTranslationKey, errorPath, ErrorActivity.LOGOUT, 'Unspecified error')
    }
}

export class RequestAuthenticationError extends BaseError {
    constructor (
        messageTranslationKeys = ['errors.request.authentication.message'],
        titleTranslationKey = 'errors.request.authentication.title',
        errorPath = '/logout'
    ) {
        super(
            messageTranslationKeys,
            titleTranslationKey,
            errorPath,
            ErrorActivity.LOGOUT,
            'Unspecified error',
            true,
            RequestTranslationErrorGroup.AUTHENTICATION
        )
    }
}

export class RequestServerError extends BaseError {
    constructor (
        messageTranslationKeys = ['errors.request.server.message'],
        titleTranslationKey = 'errors.request.server.title',
        errorPath = '/'
    ) {
        super(
            messageTranslationKeys,
            titleTranslationKey,
            errorPath,
            ErrorActivity.REDIRECT,
            'Unspecified error',
            false,
            RequestTranslationErrorGroup.SERVER
        )
    }
}

export class RequestValidationError extends BaseError {
    private readonly _errorFields: Array<string>

    constructor (
        errorFields: Array<string>,
        messageTranslationKeys = ['errors.request.validation.message'],
        titleTranslationKey = 'errors.request.validation.title',
        errorId = ''
    ) {
        super(
            messageTranslationKeys,
            titleTranslationKey,
            '',
            ErrorActivity.SHOW_ONLY_MESSAGE,
            'Unspecified error',
            false,
            RequestTranslationErrorGroup.VALIDATION,
            errorId
        )
        this._errorFields = errorFields
    }

    get errorFields (): Array<string> {
        return this._errorFields
    }
}
